import { useState } from 'react'
import { Check, PackageSearch, Pencil, Unlink } from 'lucide-react'
import { toast } from 'sonner'
import type {
  ApInvoiceLine,
  ApInvoiceSerialCorrectionInput,
} from '@/api/invoices'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { getApiErrorMessage } from '@/lib/api-error'

type PurchaseOrderLineOption = {
  id: number
  item_number: number
  display_number?: string | null
  parent_line_item_id?: number | null
  line_kind?: string | null
  model: string
  description: string
  quantity: number
  po_number?: string | null
}

type CorrectionVariables = {
  serialUuid: string
  body: ApInvoiceSerialCorrectionInput
}

export function InvoiceEquipmentSerialReview({
  lines,
  purchaseOrderLines,
  purchaseOrderLinesLoading,
  purchaseOrderLinesError,
  purchaseOrderNumber,
  correction,
  canCorrect = true,
}: {
  lines: ApInvoiceLine[]
  purchaseOrderLines: PurchaseOrderLineOption[]
  purchaseOrderLinesLoading: boolean
  purchaseOrderLinesError: boolean
  purchaseOrderNumber?: string | null
  canCorrect?: boolean
  correction: {
    isPending: boolean
    variables?: CorrectionVariables
    mutate: (
      variables: CorrectionVariables,
      callbacks?: { onSuccess?: () => void; onError?: (error: Error) => void },
    ) => void
  }
}) {
  const [editingSerialUuid, setEditingSerialUuid] = useState<string | null>(
    null,
  )
  const serials = lines.flatMap((line) => line.serials)
  // Serials on a PO line are recorded as matched; only unlinked ones are called out (USA-1747).
  const unlinkedCount = serials.filter(
    (serial) => !serial.purchase_order_line_item_id,
  ).length
  const pendingSerialUuid = correction.isPending
    ? correction.variables?.serialUuid
    : undefined

  const correct = (
    serialUuid: string,
    body: ApInvoiceSerialCorrectionInput,
    closeOnSuccess = false,
  ) => {
    correction.mutate(
      { serialUuid, body },
      {
        onSuccess: () => {
          if (closeOnSuccess) setEditingSerialUuid(null)
          toast.success('Serial mapping updated')
        },
        onError: (error) =>
          toast.error(getApiErrorMessage(error, 'Serial mapping could not be updated')),
      },
    )
  }

  return (
    <Card
      className="gap-0"
      role="region"
      aria-labelledby="equipment-serials-heading"
    >
      <CardHeader className="gap-3 pb-5 sm:flex sm:flex-row sm:items-start sm:justify-between">
        <CardTitle
          id="equipment-serials-heading"
          className="flex items-center gap-2 text-base"
        >
          <PackageSearch className="size-4" /> Equipment &amp; serials
        </CardTitle>
        {unlinkedCount > 0 ? (
          <Badge variant="outline">{unlinkedCount} not linked</Badge>
        ) : serials.length > 0 ? (
          <Badge variant="secondary">
            <Check className="mr-1 size-3" /> All matched
          </Badge>
        ) : null}
      </CardHeader>

      <CardContent className={serials.length > 0 ? 'px-0 pb-0' : undefined}>
        {serials.length === 0 ? (
          <div className="rounded-lg border border-dashed px-4 py-8 text-center">
            <p className="text-sm font-medium">No equipment serials captured</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Serials appear here when a manufacturer invoice includes them.
            </p>
          </div>
        ) : (
          <div className="divide-y border-t">
            {lines.map((line) => (
              <section
                key={line.uuid}
                className="px-6 py-5"
                aria-label={`Invoice line ${line.source_line_key}`}
              >
                <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm font-semibold">
                        {line.model_number ||
                          line.purchase_order_line_item?.model ||
                          `Line ${line.source_line_key}`}
                      </span>
                      {line.quantity != null ? (
                        <Badge variant="outline">Qty {line.quantity}</Badge>
                      ) : null}
                    </div>
                    <p className="mt-1 max-w-4xl text-sm text-muted-foreground">
                      {line.description ||
                        line.purchase_order_line_item?.description ||
                        'Invoice equipment line'}
                    </p>
                  </div>
                  {line.purchase_order_line_item ? (
                    <p className="text-xs text-muted-foreground sm:text-right">
                      PO line{' '}
                      {line.purchase_order_line_item.display_number ||
                        line.purchase_order_line_item.item_number}
                    </p>
                  ) : null}
                </div>

                <div className="mt-4 space-y-4">
                  {line.serials.map((serial) => {
                    const loading =
                      correction.isPending && pendingSerialUuid === serial.uuid
                    const selectedPoLine = serial.purchase_order_line_item_id
                      ? String(serial.purchase_order_line_item_id)
                      : null
                    const matchedPoOption = purchaseOrderLines.find(
                      (poLine) =>
                        poLine.id === serial.purchase_order_line_item_id,
                    )
                    const matchedPoLine =
                      matchedPoOption ?? serial.purchase_order_line_item
                    const choosingMatch = editingSerialUuid === serial.uuid

                    return (
                      <section
                        key={serial.uuid}
                        aria-label={`Serial ${serial.serial_raw}`}
                        className="min-w-0 border-t py-4 first:border-t-0 first:pt-0 last:pb-0"
                      >
                        <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-muted-foreground">
                              Serial number
                            </p>
                            {choosingMatch ? (
                              <POInlineEditableField
                                value={serial.serial_raw}
                                displayValue={serial.serial_raw}
                                ariaLabel={`Edit serial ${serial.serial_raw}`}
                                pending={correction.isPending}
                                showEditIcon
                                buttonClassName="min-h-11 px-0 font-mono font-semibold hover:bg-transparent sm:min-h-8"
                                inputClassName="min-h-11 sm:h-7 sm:min-h-0"
                                onConfirm={(value) => {
                                  if (!value.trim()) {
                                    toast.error('Serial number cannot be blank')
                                    return
                                  }
                                  correct(
                                    serial.uuid,
                                    {
                                      serial_number: value.trim(),
                                      reason:
                                        'Corrected serial number during invoice review',
                                    },
                                    true,
                                  )
                                }}
                              />
                            ) : (
                              <p className="mt-2 font-mono text-sm font-semibold">
                                {serial.serial_raw}
                              </p>
                            )}
                          </div>
                          {!choosingMatch ? (
                            <div className="flex items-center gap-2">
                              {serial.purchase_order_line_item_id ? (
                                <Badge variant="secondary">
                                  <Check className="mr-1 size-3" /> Matched
                                </Badge>
                              ) : (
                                <Badge variant="outline">Not linked</Badge>
                              )}
                              {canCorrect ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="min-h-11 sm:min-h-8"
                                  disabled={correction.isPending}
                                  onClick={() =>
                                    setEditingSerialUuid(serial.uuid)
                                  }
                                >
                                  <Pencil className="mr-1 size-3.5" /> Correct
                                </Button>
                              ) : null}
                            </div>
                          ) : null}
                        </div>

                        {choosingMatch ? (
                          <div
                            className="mt-3 rounded-lg bg-muted/40 p-3 sm:p-4"
                            aria-busy={loading}
                          >
                            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                              <p className="text-sm font-semibold">
                                Correct serial or equipment
                              </p>
                              {editingSerialUuid === serial.uuid ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="min-h-11 sm:min-h-8"
                                  disabled={correction.isPending}
                                  onClick={() => setEditingSerialUuid(null)}
                                >
                                  Cancel
                                </Button>
                              ) : null}
                            </div>
                            {purchaseOrderLinesLoading ? (
                              <p className="text-sm text-muted-foreground">
                                Loading PO equipment…
                              </p>
                            ) : null}
                            {purchaseOrderLinesError ? (
                              <p className="text-sm text-destructive">
                                PO equipment could not be loaded.
                              </p>
                            ) : null}
                            {!purchaseOrderLinesLoading &&
                            !purchaseOrderLinesError &&
                            purchaseOrderLines.length === 0 ? (
                              <p className="text-sm text-muted-foreground">
                                No equipment lines are available on the linked
                                PO.
                              </p>
                            ) : null}
                            {!purchaseOrderLinesLoading &&
                            !purchaseOrderLinesError &&
                            purchaseOrderLines.length > 0 ? (
                              <ReviewChoiceGroup
                                label={`PO equipment for serial ${serial.serial_raw}`}
                                value={selectedPoLine}
                                disabled={correction.isPending}
                                className="grid w-full grid-cols-1 gap-1.5 bg-transparent p-0"
                                optionClassName="min-h-11 w-full justify-start whitespace-normal border bg-background px-3 py-2 text-left shadow-xs"
                                options={purchaseOrderLines.map((poLine) => ({
                                  value: String(poLine.id),
                                  label: `${poLine.po_number || purchaseOrderNumber || 'PO'} · Line ${poLine.display_number || poLine.item_number} · ${poLine.model} · Qty ${poLine.quantity}`,
                                }))}
                                onChange={(value) => {
                                  correct(
                                    serial.uuid,
                                    {
                                      purchase_order_line_item_id:
                                        Number(value),
                                      review_status: 'confirmed',
                                      match_confidence: 1,
                                      reason:
                                        'Selected equipment match during invoice review',
                                    },
                                    true,
                                  )
                                }}
                              />
                            ) : null}
                            {serial.purchase_order_line_item_id ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="mt-2 min-h-11 text-muted-foreground sm:min-h-8"
                                disabled={correction.isPending}
                                onClick={() => {
                                  correct(
                                    serial.uuid,
                                    {
                                      purchase_order_line_item_id: null,
                                      internal_order_line_item_id: null,
                                      review_status: 'unmatched',
                                      reason:
                                        'Removed equipment match during invoice review',
                                    },
                                    true,
                                  )
                                }}
                              >
                                <Unlink className="mr-1 size-3.5" /> Remove PO
                                link
                              </Button>
                            ) : null}
                          </div>
                        ) : serial.purchase_order_line_item_id ? (
                          <div className="mt-2 grid min-w-0 gap-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-6">
                            <p className="line-clamp-2 text-sm font-medium">
                              {matchedPoOption?.po_number ||
                                purchaseOrderNumber ||
                                'PO'}{' '}
                              · Line{' '}
                              {matchedPoLine?.display_number ||
                                matchedPoLine?.item_number ||
                                line.purchase_order_line_item?.display_number ||
                                line.purchase_order_line_item?.item_number}{' '}
                              ·{' '}
                              {matchedPoLine?.model ||
                                line.purchase_order_line_item?.model}
                            </p>
                            {serial.internal_order_line_item?.title ? (
                              <p className="line-clamp-2 text-sm text-muted-foreground">
                                {serial.internal_order_line_item.title}
                              </p>
                            ) : null}
                          </div>
                        ) : (
                          <p className="mt-2 text-sm text-muted-foreground">
                            No PO equipment linked
                          </p>
                        )}
                      </section>
                    )
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
